import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { RouteComponentProps } from "react-router";
import { apiClient } from "../../App";
import IRecording from "../../../../shared/IRecording";
import MessageSequenceRow from "../../components/MessageSequenceRow";
import RecordingStatusBar from "../../components/RecordingStatusBar";
import IMessageSequence from "../../../../shared/IMessageSequence";
import Help from "../../components/Help";
import { useHotkeys } from "react-hotkeys-hook";
import ActiveFilter from "../../components/ActiveFilter";

const StatusFilters = [
  {
    key: "2",
    start: 200,
    end: 299,
    activeMessage: "Showing 2xx",
    description: "'2' filter 2xx status",
  },
  {
    key: "3",
    start: 300,
    end: 399,
    activeMessage: "Showing 3xx",
    description: "'3' filter 3xx status",
  },
  {
    key: "4",
    start: 400,
    end: 499,
    activeMessage: "Showing 4xx",
    description: "'4' filter 4xx status",
  },
  {
    key: "5",
    start: 500,
    end: 599,
    activeMessage: "Showing 5xx",
    description: "'5' filter 5xx status",
  },
];

interface MatchParams {
  id: string;
}

interface Props extends RouteComponentProps<MatchParams> {}

const StyledRecording = styled.div`
  .messages {
    padding-top: 2rem;
    padding-bottom: 5rem;
    height: 100vh;
    overflow-y: scroll;
  }
`;

const TIMER_INTERVAL_MS = 2000;

const Recording: React.FC<Props> = ({ match, history }) => {
  const [recording, setRecording] = useState<IRecording | null>(null);
  const [statusFilter, setStatusFilter] = useState<any | null>(null);
  const id = match.params.id;
  let timer: number;

  const loadRecording = async () => {
    const { data: recording } = await apiClient.get<IRecording>(`/recording/${id}`);
    setRecording(recording);

    if (recording.state === "running") {
      timer = setTimeout(loadRecording, TIMER_INTERVAL_MS);
    }
  };

  const stopRecording = async () => {
    const { data: recording } = await apiClient.post<IRecording>(`/recording/${id}/stop`, {});
    setRecording(recording);
    if (timer) {
      clearTimeout(timer);
    }
  };

  const openMessageSequence = (messageSequence: IMessageSequence) => {
    history.push(`/recording/${id}/${messageSequence.reqId}`);
  };

  useEffect(() => {
    if (!recording) {
      loadRecording();
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  // Register filters/keyboard shortcuts
  // TODO: Is there a way of looping and still be compliant with react hooks?
  useHotkeys(StatusFilters[0].key, () => setStatusFilter(statusFilter === StatusFilters[0] ? null : StatusFilters[0]), [
    statusFilter,
  ]);
  useHotkeys(StatusFilters[1].key, () => setStatusFilter(statusFilter === StatusFilters[1] ? null : StatusFilters[1]), [
    statusFilter,
  ]);
  useHotkeys(StatusFilters[2].key, () => setStatusFilter(statusFilter === StatusFilters[2] ? null : StatusFilters[2]), [
    statusFilter,
  ]);
  useHotkeys(StatusFilters[3].key, () => setStatusFilter(statusFilter === StatusFilters[3] ? null : StatusFilters[3]), [
    statusFilter,
  ]);

  const getMessageSequences = () => {
    if (!recording) return [];

    if (statusFilter) {
      return recording.messageSequences.filter(({ status }) => {
        if (!status) return false;
        return status >= statusFilter.start && status <= statusFilter.end;
      });
    }
    return recording.messageSequences;
  };

  return (
    <StyledRecording>
      <RecordingStatusBar
        loading={!recording || recording.state === "running"}
        onStop={stopRecording}
        query={recording ? recording.query : ""}
      />
      <div className="messages">
        {getMessageSequences().map((ms) => (
          <MessageSequenceRow key={ms.reqId} messageSequence={ms} onClick={openMessageSequence} />
        ))}
      </div>
      <Help rows={StatusFilters.map((filter) => filter.description)} />
      {statusFilter && <ActiveFilter filters={[statusFilter.activeMessage]} />}
    </StyledRecording>
  );
};

export default Recording;
